import { deleteField } from 'firebase/firestore';
import type { Chef } from '../types';
import { normalizeChefPayload } from './chefCsv';
import { getChefWebsites } from './chefDisplay';

interface ChefLinksSnapshot {
    websites: readonly string[];
    website: string | null;
    instagramUsername: string | null;
}

function snapshotFromChef(chef: Chef): ChefLinksSnapshot {
    return {
        websites: chef.websites ?? [],
        website: chef.website?.trim() || null,
        instagramUsername: chef.instagramUsername?.trim() || null,
    };
}

function snapshotFromNormalized(chef: Chef): ChefLinksSnapshot {
    const mergedLinks = getChefWebsites(chef);
    const normalized = normalizeChefPayload({
        websites: mergedLinks,
        instagramUsername: chef.instagramUsername,
        instagramUserId: chef.instagramUserId,
    });

    return {
        websites: normalized.websites ?? [],
        website: normalized.website?.trim() || null,
        instagramUsername: normalized.instagramUsername?.trim() || null,
    };
}

function snapshotsEqual(a: ChefLinksSnapshot, b: ChefLinksSnapshot): boolean {
    if (a.website !== b.website) return false;
    if (a.instagramUsername !== b.instagramUsername) return false;
    if (a.websites.length !== b.websites.length) return false;
    return a.websites.every((link, index) => link === b.websites[index]);
}

export function shouldMigrateChefLinks(chef: Chef): boolean {
    return !snapshotsEqual(snapshotFromChef(chef), snapshotFromNormalized(chef));
}

export function collectChefLinkMigrationCandidates(chefs: readonly Chef[]): Chef[] {
    return chefs.filter(shouldMigrateChefLinks);
}

/** Returns a Firestore update patch, or null when the chef is already migrated. */
export function buildChefLinksMigrationPatch(chef: Chef, now = new Date().toISOString()): Record<string, unknown> | null {
    if (!shouldMigrateChefLinks(chef)) return null;

    const mergedLinks = getChefWebsites(chef);
    const normalized = normalizeChefPayload({
        websites: mergedLinks,
        instagramUsername: chef.instagramUsername,
        instagramUserId: chef.instagramUserId,
    });

    const update: Record<string, unknown> = { updatedAt: now };

    if (normalized.websites?.length) {
        update.websites = normalized.websites;
        update.website = normalized.website;
    } else {
        update.websites = deleteField();
        update.website = deleteField();
    }

    if (normalized.instagramUsername) {
        update.instagramUsername = normalized.instagramUsername;
    }

    return update;
}
